import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  MaterialReactTable,
  MRT_EditActionButtons,
  type MaterialReactTableProps,
  type MRT_ColumnDef,
  type MRT_Row,
} from 'material-react-table';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit, LockReset } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BusinessUserDetails } from './makeData';
import { useAPI } from '../../shared/services/api/API';
import { WASHUB_CONSTANTS } from '../../shared/Constants';

export const BusinessUser = () => {
  const { httpGet, httpPost, httpPut, httpDelete, handleAlertBar } = useAPI();
  const [tableData, setTableData] = useState<BusinessUserDetails[]>([]);
  const [campData, setCampData] = useState([]);
  const [businessData, setBusinessData] = useState([]);
  const [openNewPasswordDialog, setOpenNewPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [selectedUser, setSelectedUser] = useState<any | null>(null);

  const [validationErrors, setValidationErrors] = useState<{
    [cellId: string]: string;
  }>({});
  const navigate = useNavigate();

  // GET with API : User
  const fetchUser = async () => {
    const urlToGet = WASHUB_CONSTANTS.API_URLS.BUSINESS_USER_DATA_GET;
    httpGet(urlToGet).then((response) => {
      if (response.status === 200) {
        setTableData(response.data);
      } else {
        if (response.data.error) {
          console.error('Error fetching User: ', response.data.message);
          setTableData([]);
        }
      }
    });
  };

  useEffect(() => {
    // GET with API : Collection Center
    const fetchCollectionCenter = async () => {
      httpGet(WASHUB_CONSTANTS.API_URLS.BUSINESS_USER_CC_DATA_GET).then(
        (response) => {
          if (response.status === 200) {
            setCampData(response?.data);
          } else {
            if (response.data.error) {
              console.error('Error fetching role: ', response.data.message);
              setCampData([]);
            }
          }
        },
      );
    };

    // GET with API : Department
    const fetchBusiness = async () => {
      const urlToGetDept = WASHUB_CONSTANTS.API_URLS.BUSINESS_DATA_GET;
      httpGet(urlToGetDept).then((response) => {
        if (response.status === 200) {
          const sortBusinessArray = response?.data?.sort((a: any, b: any) =>
            a?.name.toLowerCase().localeCompare(b.name.toLowerCase()),
          );
          setBusinessData(sortBusinessArray);
        } else {
          if (response.data.error) {
            console.error('Error fetching department: ', response.data.message);
            setBusinessData([]);
          }
        }
      });
    };

    fetchCollectionCenter();
    fetchBusiness();
    fetchUser();
  }, []);

  // PUT with API
  const handleSaveRowEdits: MaterialReactTableProps<BusinessUserDetails>['onEditingRowSave'] =
    async ({ exitEditingMode, row, values }) => {
      if (!Object.keys(validationErrors).length) {
        const payload = {
          firstName: valuesDetail?.firstName || null,
          lastName: valuesDetail?.lastName || null,
          phone: valuesDetail?.phone || null,
          campId: valuesDetail?.campId || null,
          userId: valuesDetail?.userId || null,
          password: valuesDetail?.password || null,
          businessId: valuesDetail['business.name'] || null,
        };

        httpPut(
          WASHUB_CONSTANTS.API_URLS.UPDATE_BUSINESSS_USER_DATA_PUT +
            '/' +
            values?.id,
          payload,
        ).then((response) => {
          console.log('jwtToken :', localStorage.getItem('jwtToken'));
          console.log('RESPONSE :', response);
          if (response.status === 200) {
            handleAlertBar('success', `Business User updated Successfully`);
            setValuesDetail({});
            fetchUser();
            exitEditingMode(); //required to exit editing mode and close modal
          } else {
            if (response.data.error) {
              console.error('Error Updating User: ', response.data.message);
            }
          }
        });
      }
    };

  const handleCancelRowEdits = () => {
    setValidationErrors({});
  };

  const columns = useMemo<MRT_ColumnDef<BusinessUserDetails>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        enableColumnOrdering: false,
        enableEditing: false, //disable editing on this column
        enableHiding: false,
        enableSorting: false,
        size: 80,
      },
      {
        accessorKey: 'userId',
        header: 'User Id',
        enableEditing: false,
        size: 140,
      },
      {
        accessorKey: 'firstName',
        header: 'First Name',
        size: 140,
      },
      {
        accessorKey: 'lastName',
        header: 'Last Name',
        size: 140,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
      },
      {
        accessorKey: 'campId',
        header: 'CampId',
        editVariant: 'select',
        editSelectOptions: campData,
        muiEditTextFieldProps: {
          select: true,
        },
      },
      {
        accessorKey: 'business.name',
        header: 'Business',
        editVariant: 'select',
        editSelectOptions: businessData,
        muiEditTextFieldProps: {
          select: true,
        },
      },
    ],
    [],
  );

  const createNewUser = () => {
    const column = [
      {
        accessorKey: 'userId',
        header: 'User Id',
        size: 140,
      },
      {
        accessorKey: 'firstName',
        header: 'First Name',
        size: 140,
      },
      {
        accessorKey: 'lastName',
        header: 'Last Name',
        size: 140,
      },
      {
        accessorKey: 'password',
        header: 'Password',
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
      },
      {
        accessorKey: 'business.name',
        header: 'Business',
        editVariant: 'select',
        editSelectOptions: businessData,
        muiEditTextFieldProps: {
          select: true,
        },
      },
      {
        accessorKey: 'campId',
        header: 'Camp Id',
        editVariant: 'select',
        editSelectOptions: campData,
        muiEditTextFieldProps: {
          select: true,
        },
      },
    ];
    navigate('create-business-user', {
      state: {
        column: column,
        businessData: businessData,
        campData: campData,
      },
    });
  };

  const [valuesDetail, setValuesDetail] = useState<any>(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = '';
      return acc;
    }, {} as any),
  );

  const openPasswordDialog = (user: any) => {
      setOpenNewPasswordDialog(true);
      setSelectedUser(user);
    }
  
    const handlePasswordReset = (newPass: any) => {
      httpPut(WASHUB_CONSTANTS.API_URLS.BUSINESSUSER_PASSWORD_RESET_PUT+'/'+selectedUser?.id, { password: newPass, userId: selectedUser?.userId }).then((response) => {
          if (response.status === 200) {
            handleAlertBar(
              'success',
              `${selectedUser?.firstName} ${selectedUser?.lastName} Password Reset Successfully`,
            );
            setOpenNewPasswordDialog(false);
            setNewPassword('');
          }
      });
    }

  return (
    <div style={{ width: '100%' }}>
      <Grid container justifyContent={'space-between'}>
        <Grid item className="mb-1 mt-1 ms-3">
          <Typography
            color={'primary'}
            sx={{ textAlign: 'center' }}
            variant="h5"
            className="mt-0"
          >
            Business User Management
          </Typography>
        </Grid>
        <Grid item className="mb-1 mt-1 me-3">
          <Button
            color="primary"
            sx={{ color: 'white' }}
            onClick={createNewUser}
            variant="contained"
          >
            Create New Business User
          </Button>
        </Grid>
      </Grid>

      <div style={{ padding: '5px 15px' }}>
        <MaterialReactTable
          displayColumnDefOptions={{
            'mrt-row-actions': {
              muiTableHeadCellProps: {
                align: 'center',
              },
              size: 100,
            },
          }}
          columns={columns}
          data={tableData}
          enableFullScreenToggle={false}
          enableStickyHeader
          enableColumnOrdering
          enableEditing
          enableHiding
          editDisplayMode="modal"
          onEditingRowSave={handleSaveRowEdits}
          onEditingRowCancel={handleCancelRowEdits}
          muiTableContainerProps={{
            sx: {
              height: '64vh',
            },
          }}
          renderEditRowDialogContent={({
            table,
            row,
            internalEditComponents,
          }) => (
            <Dialog fullWidth open={true}>
              <DialogTitle
                sx={{ backgroundColor: '#83CBEB', color: '#ffff' }}
                variant="h5"
                textAlign={'center'}
                className="p-3 mb-3"
              >
                Update User
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={3}>
                  {columns.map((column) => (
                    <>
                      {column.accessorKey === 'userId' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={6}
                          lg={6}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.userId}
                            // disabled={true}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'firstName' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={6}
                          lg={6}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.firstName}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'lastName' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={6}
                          lg={6}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.lastName}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {/* {column.accessorKey === 'password' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={6}
                          lg={6}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.password}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )} */}
                      {column.accessorKey === 'phone' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={6}
                          lg={6}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.phone}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'business.name' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={6}
                          lg={6}
                        >
                          <TextField
                            key={column.accessorKey}
                            fullWidth
                            label={column.header}
                            name={column.accessorKey}
                            variant="standard"
                            select
                            defaultValue={row.original?.business?.id}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          >
                            {businessData.map((option: any) => (
                              <MenuItem key={option?.id} value={option?.id}>
                                {option?.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                      {column.accessorKey === 'campId' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={6}
                          lg={6}
                        >
                          <TextField
                            key={column.accessorKey}
                            fullWidth
                            label={column.header}
                            name={column.accessorKey}
                            variant="standard"
                            select
                            defaultValue={row.original?.campId}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          >
                            {campData.map((option: any) => (
                              <MenuItem key={option.id} value={option.campId}>
                                {option.campId}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                    </>
                  ))}
                </Grid>
              </DialogContent>
              <DialogActions>
                <MRT_EditActionButtons variant="text" table={table} row={row} />
              </DialogActions>
            </Dialog>
          )}
          renderRowActions={({ row, table }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              <Tooltip arrow placement="right" title="Edit">
                <IconButton
                  onClick={() => {
                    table.setEditingRow(row);
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip arrow placement="right" title="Reset Password">
                <IconButton
                  onClick={() => openPasswordDialog(row?.original)}
                >
                  <LockReset />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        />
      </div>
<Dialog
              fullWidth
              maxWidth="sm"
              open={openNewPasswordDialog}
              onClose={() => setOpenNewPasswordDialog(false)}
            >
              <DialogTitle textAlign="center">Reset Password</DialogTitle>
              <DialogContent>
                <TextField
                  size="small"
                  key="newPassword"
                  style={{ width: '100%', marginTop: '5px' }}
                  label="Enter New Password"
                  name="newPassword"
                  type="password"
                  onChange={(e) => setNewPassword(e.target.value)}
                  value={newPassword}
                />
              </DialogContent>
                      <DialogActions className="dialog-btn">
                        <Button
                          color="error"
                          variant="contained"
                          sx={{ color: 'white' }}
                          onClick={() => setOpenNewPasswordDialog(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => handlePasswordReset(newPassword)}
                          sx={{ color: 'white' }}
                          variant="contained"
                        >
                          {'Reset Password'}
                        </Button>
                      </DialogActions>
            </Dialog>
    </div>
  );
};

export default BusinessUser;
